import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../../config/app';
import type { ContactFormData, ServiceType } from './types';
import { BUDGET_OPTIONS } from './types';

const WHATSAPP_URL = 'https://wa.me/33626748299';

const initialState: ContactFormData = {
  service: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  budget: undefined,
  message: '',
  consent: false,
};

export const ContactForm: React.FC = () => {
  const [form, setForm] = useState<ContactFormData>(initialState);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = <K extends keyof ContactFormData>(field: K, value: ContactFormData[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error('Veuillez accepter la politique de confidentialité.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur envoi');
      setSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center gap-4 py-12 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-[#5865F2]/20 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-[#5865F2]" />
        </div>
        <h3 className="text-white font-bold text-lg">Message envoyé !</h3>
        <p className="text-gray-400 text-sm max-w-xs">
          Nous vous répondrons sous 48h. Un email de confirmation vous a été envoyé.
        </p>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 bg-green-600/20 border border-green-500/30 text-green-400 rounded-lg text-sm font-medium hover:bg-green-600/30 transition-colors"
        >
          Besoin d'une réponse immédiate ? WhatsApp →
        </a>
      </motion.div>
    );
  }

  const inputClass =
    'w-full bg-[#2F3136] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#5865F2]/60 transition-colors';
  const labelClass = 'block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot anti-spam */}
      <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" />

      {/* Service selector */}
      <div>
        <label className={labelClass}>Je cherche...</label>
        <select
          required
          value={form.service}
          onChange={(e) => set('service', e.target.value as ServiceType | '')}
          className={inputClass}
        >
          <option value="" disabled>Sélectionner un service</option>
          <option value="depannage">🔧 Dépannage informatique</option>
          <option value="site-web">🌐 Création de site web</option>
          <option value="autre">❓ Autre demande</option>
        </select>
      </div>

      {/* Champ budget conditionnel (site web uniquement) */}
      <AnimatePresence>
        {form.service === 'site-web' && (
          <motion.div
            key="budget"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <label className={labelClass}>Budget approximatif</label>
            <select
              value={form.budget ?? ''}
              onChange={(e) =>
                set('budget', e.target.value as typeof BUDGET_OPTIONS[number]['value'] | undefined)
              }
              className={inputClass}
            >
              <option value="">Non défini</option>
              {BUDGET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Prénom + Nom */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Prénom</label>
          <input
            required
            type="text"
            placeholder="Jean"
            value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Nom</label>
          <input
            required
            type="text"
            placeholder="Dupont"
            value={form.lastName}
            onChange={(e) => set('lastName', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {/* Téléphone */}
      <div>
        <label className={labelClass}>Téléphone</label>
        <input
          type="tel"
          placeholder="+33 6 ..."
          value={form.phone}
          onChange={(e) => set('phone', e.target.value)}
          className={inputClass}
        />
      </div>

      {/* Email */}
      <div>
        <label className={labelClass}>Email</label>
        <input
          required
          type="email"
          placeholder="jean@exemple.com"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          className={inputClass}
        />
      </div>

      {/* Message */}
      <div>
        <label className={labelClass}>Message</label>
        <textarea
          required
          rows={4}
          placeholder="Décrivez votre besoin..."
          value={form.message}
          onChange={(e) => set('message', e.target.value)}
          className={`${inputClass} resize-none`}
          minLength={10}
        />
      </div>

      {/* Consentement RGPD */}
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => set('consent', e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#5865F2] cursor-pointer flex-shrink-0"
        />
        <span className="text-xs text-gray-500 leading-relaxed">
          J'accepte que mes données soient utilisées pour traiter ma demande conformément à la{' '}
          <Link to="/politique-de-confidentialite" className="text-[#5865F2] hover:underline">
            politique de confidentialité
          </Link>.
        </span>
      </label>

      {/* CTA bouton */}
      <button
        type="submit"
        disabled={loading}
        className="relative w-full py-3 px-6 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-bold rounded-lg text-sm hover:scale-[1.02] hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent" />
        <span className="relative z-10 flex items-center justify-center gap-2">
          {loading ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Send size={15} />
          )}
          {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
        </span>
      </button>

      <p className="text-xs text-gray-600 text-center">Données confidentielles · Réponse sous 48h</p>
    </form>
  );
};
